import React, { useEffect, useState } from "react";
import type {
  CategoryBlockData,
  SavedReport,
  CurrentYearStatus,
  ProductCategoryJson,
  ReportDiff,
  DiffRow,
} from "../types";
import {
  validateReport,
  hasMissingVolume,
  hasValidProductEntry,
  normalizeVolume,
} from "../utils/validation";
import {
  getProducerReportsByYears,
  saveReport,
  confirmReport,
  getReportsByProducer,
  getReportById,
} from "../api/actorDashboardApi";
import {
  cn,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import Tabs from "../components/DashboardTabs";
import ReportInputView from "./ReportInputView";
import ReportSummaryView from "./ReportSummaryView";
import ReportSuccessView from "./ReportSuccessView";
import ReportHistoryView from "./ReportHistoryView";
import UserInfo, { VippsUserInfo } from "../../Dashboard/UserInfo";
import { DashboardAlertBox } from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import { DashboardCard } from "@/ReactInit/Scripts/DashboardPage/components/DashboardCard";
import { DashboardButton } from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {
  formatDate,
  formatVolumeKg,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import { useIsMobile } from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";
import { DashboardLoader } from "@/ReactInit/Scripts/DashboardPage/components/DashboardLoader";
import { getReportYears } from "@/ReactInit/Scripts/DashboardPage/utils/reportYears";

// Helpers and constants
const createEmptyCategoryBlock = (): CategoryBlockData => ({
  id: generateCategoryId(),
  category: null,
  products: [],
  volumes: [],
  lastYearVolumes: [],
});

let categoryIdCounter = 0;
const generateCategoryId = () => `cat-${categoryIdCounter++}`;

const { currentYear, previousYear } = getReportYears();

const getCategoryForProduct = (
  product: string,
  cats: ProductCategoryJson[]
): string | null => {
  const lower = normalizeProductKey(product);
  for (const c of cats) {
    for (const productName of Object.keys(c.products)) {
      if (productName.trim().toLowerCase() === lower) return c.category;
    }
  }
  return null;
};

const buildCategoryBlocksFromReport = (
  report: SavedReport,
  cats: ProductCategoryJson[]
): CategoryBlockData[] => {
  const numbers = report.numbers ?? [];
  if (numbers.length === 0) return [createEmptyCategoryBlock()];

  const byCategory = new Map<
    string,
    {
      products: string[];
      volumes: string[];
      lastYearVolumes: (number | null)[];
    }
  >();

  for (const n of numbers) {
    const category =
      getCategoryForProduct(n.productName, cats) ?? "Ukjent kategori";
    const entry = byCategory.get(category) ?? {
      products: [],
      volumes: [],
      lastYearVolumes: [],
    };
    entry.products.push(n.productName);
    entry.volumes.push(String(n.volume));
    entry.lastYearVolumes.push(null);
    byCategory.set(category, entry);
  }

  return Array.from(byCategory.entries()).map(([category, data]) => ({
    id: generateCategoryId(),
    category,
    products: data.products,
    volumes: data.volumes,
    lastYearVolumes: data.lastYearVolumes,
  }));
};

const ActorDashboardPage = () => {
  // Authentication by Vipps
  const [vippsUser, setVippsUser] = useState<VippsUserInfo | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const userAuthenticated = !!vippsUser?.isAuthenticated;

  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isPageBootstrapped, setIsPageBootstrapped] = useState(false);

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isEditingConfirmed, setIsEditingConfirmed] = useState(false);

  const [currentYearStatus, setCurrentYearStatus] = useState<CurrentYearStatus>(
    { kind: "none" }
  );
  const [productCategories, setProductCategories] = useState<
    ProductCategoryJson[]
  >([]);
  const [isLoadingBootstrap, setIsLoadingBootstrap] = useState(false);

  const [conflictActionError, setConflictActionError] = useState<string | null>(
    null
  );
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);

  const [publishGuardError, setPublishGuardError] = useState<string | null>(
    null
  );

  const [bootstrapNonce, setBootstrapNonce] = useState(0);

  const refreshBootstrap = () => {
    setIsPageBootstrapped(false);
    setBootstrapNonce((n) => n + 1);
  };

  const isMobile = useIsMobile();

  useEffect(() => {
    fetch("/dashboard/json/productCategories.json")
      .then((res) => res.json())
      .then(setProductCategories)
      .catch(console.error);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("access_denied") === "true") {
      setLoginError(
        "Telefonnummeret ditt er ikke godkjent for innlogging. Hvis du mener dette er feil, vennligst ta kontakt med oss."
      );
    }

    (async () => {
      try {
        setIsAuthLoading(true);
        const res = await fetch("/api/auth/vippsuserinfo", {
          credentials: "include",
        });
        const data = (await res.json()) as VippsUserInfo;
        if (!cancelled) setVippsUser(data);
      } catch {
        if (!cancelled) setVippsUser({ isAuthenticated: false });
      } finally {
        if (!cancelled) setIsAuthLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // UI states and helpers
  const actorTabs = ["Innrapportering", "Rapporthistorikk"];
  const [activeTab, setActiveTab] = useState("Innrapportering");
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const prevStep = () => setStep(1);
  const nextStep = async () => {
    const validationErrors = validateReport(categoryBlocks);

    if (
      validationErrors.length > 0 ||
      hasMissingVolume(categoryBlocks) ||
      !hasValidProductEntry(categoryBlocks) ||
      hasRowErrors
    ) {
      setValidationErrors(validationErrors);
      return;
    }
    if (currentYearStatus.kind === "confirmed" && isEditingConfirmed) {
      setStep(2);
    } else {
      await saveDraft();
      setStep(2);
    }
  };
  const goToReportHistory = () => {
    setActiveTab("Rapporthistorikk");
    setStep(1);
    refreshBootstrap();
  };

  // Input states
  const [categoryBlocks, setCategoryBlocks] = useState<CategoryBlockData[]>([
    createEmptyCategoryBlock(),
  ]);
  const [comment, setComment] = useState("");
  const [savedReport, setSavedReport] = useState<SavedReport | null>(null);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");

  // Validation and errors
  const [rowErrors, setRowErrors] = useState<Record<string, boolean[]>>({});
  const [validationErrors, setValidationErrors] = useState<string[] | null>(
    null
  );
  if (validationErrors) console.error("Validation errors:", validationErrors);
  const hasRowErrors = Object.values(rowErrors).some((rowList) =>
    rowList.some((v) => v)
  );

  // Reports and previous year's reports
  const [reportsByYear, setReportsByYear] = useState<
    Record<number, SavedReport>
  >({});
  const [lastYearVolumesByProduct, setLastYearVolumesByProduct] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (!vippsUser?.producerId) return;
    if (productCategories.length === 0) return;

    const producerId = vippsUser.producerId;
    let cancelled = false;

    const ensureFull = async (r: SavedReport): Promise<SavedReport> => {
      // list endpoint might return full, but we don't trust it
      if (r.numbers && Array.isArray(r.numbers)) return r;
      return await getReportById(r.reportId);
    };

    (async () => {
      try {
        setPublishGuardError(null);
        setIsLoadingBootstrap(true);
        setIsPageBootstrapped(false);

        const list = await getReportsByProducer(producerId);
        if (cancelled) return;

        const currentYearReports = list
          .filter((r) => r.year === currentYear)
          .filter((r) => r.year > 0);

        if (currentYearReports.length === 0) {
          setCurrentYearStatus({ kind: "none" });
          setSavedReport(null);
          setCategoryBlocks([createEmptyCategoryBlock()]);
          setComment("");
          setStep(1);
        } else {
          const draftStub = currentYearReports.find((r) => !r.reportDate);
          const confirmedStub = currentYearReports.find((r) => !!r.reportDate);

          if (draftStub && confirmedStub) {
            const [draftFull, confirmedFull] = await Promise.all([
              ensureFull(draftStub),
              ensureFull(confirmedStub),
            ]);
            if (cancelled) return;

            const confirmedPublished = !!confirmedFull.publishDate;
            const diff = diffReports(draftFull, confirmedFull);

            setCurrentYearStatus({
              kind: "conflict",
              year: currentYear,
              draft: draftFull,
              confirmed: confirmedFull,
              confirmedPublished,
              diff,
            });

            if (confirmedPublished) {
              setSavedReport(confirmedFull);
              setComment(confirmedFull.comment ?? "");
              setCategoryBlocks([createEmptyCategoryBlock()]);
              setStep(1);
            } else {
              setSavedReport(draftFull);
              setComment(draftFull.comment ?? "");
              setCategoryBlocks(
                buildCategoryBlocksFromReport(draftFull, productCategories)
              );
              setStep(1);
            }
          } else {
            const chosenStub =
              confirmedStub ?? draftStub ?? currentYearReports[0];

            const full = await ensureFull(chosenStub);
            if (cancelled) return;

            const isDraft = !full.reportDate;

            if (isDraft) {
              setCurrentYearStatus({ kind: "draft", report: full });
              setSavedReport(full);
              setComment(full.comment ?? "");
              setCategoryBlocks(
                buildCategoryBlocksFromReport(full, productCategories)
              );
              setStep(1);
            } else {
              setCurrentYearStatus({ kind: "confirmed", report: full });
              setSavedReport(full);
              setComment(full.comment ?? "");
              setStep(1);
            }
          }
        }

        const byYear = await getProducerReportsByYears(producerId, [
          currentYear,
          previousYear,
        ]);
        if (!cancelled) setReportsByYear(byYear);

        const lastYearReport = byYear[previousYear];
        if (lastYearReport?.numbers) {
          const map: Record<string, number> = {};
          lastYearReport.numbers.forEach((n) => {
            const key = normalizeProductKey(n.productName);
            map[key] = n.volume;
          });
          if (!cancelled) setLastYearVolumesByProduct(map);
        } else {
          if (!cancelled) setLastYearVolumesByProduct({});
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) {
          setIsLoadingBootstrap(false);
          setIsPageBootstrapped(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [vippsUser?.producerId, productCategories.length, bootstrapNonce]);

  // Category and product helpers
  const addCategoryBlock = () => {
    setCategoryBlocks((prev) => [...prev, createEmptyCategoryBlock()]);
  };
  const removeCategoryBlock = (blockId: string) => {
    setCategoryBlocks((prev) =>
      prev.length > 1 ? prev.filter((b) => b.id !== blockId) : prev
    );
  };
  const updateCategoryBlock = (
    blockIndex: number,
    update: (block: CategoryBlockData) => CategoryBlockData
  ) => {
    setCategoryBlocks((prev) =>
      prev.map((block, i) => (i === blockIndex ? update(block) : block))
    );
  };
  const updateCategory = (blockIndex: number, category: string | null) => {
    updateCategoryBlock(blockIndex, (block) => {
      const prev = block.category ?? null;
      const next = category && category.trim() ? category : null;

      if (prev === next) return block;

      return {
        ...block,
        category: next,
        products: [],
        volumes: [],
        lastYearVolumes: [],
      };
    });
  };

  const updateBlockRow = (
    blockIndex: number,
    field: "products" | "volumes" | "lastYearVolumes",
    rowIndex: number,
    value: string | number | null
  ) => {
    updateCategoryBlock(blockIndex, (block) => {
      const arr = [...block[field]];
      arr[rowIndex] = value;
      return { ...block, [field]: arr };
    });
  };
  const updateProduct = (
    blockIndex: number,
    rowIndex: number,
    product: string
  ) => updateBlockRow(blockIndex, "products", rowIndex, product);

  const updateVolume = (blockIndex: number, rowIndex: number, volume: string) =>
    updateBlockRow(blockIndex, "volumes", rowIndex, volume);

  const updateLastYearVolume = (
    blockIndex: number,
    rowIndex: number,
    value: number | null
  ) => updateBlockRow(blockIndex, "lastYearVolumes", rowIndex, value);

  // API payload, save and submit
  const buildApiPayload = () => {
    const numbers: { productName: string; volume: number }[] = [];

    categoryBlocks.forEach((block) => {
      block.products.forEach((prod, idx) => {
        const productName = (prod || "").trim();
        const normalizedVolume = normalizeVolume(block.volumes[idx]);

        if (!productName || !normalizedVolume) return;

        const volume = Number(normalizedVolume);
        if (!isNaN(volume)) {
          numbers.push({ productName, volume });
        }
      });
    });

    return {
      reportId: savedReport?.reportId ?? null,
      producerId: vippsUser?.producerId ?? "",
      producerName: vippsUser?.producerName ?? null,
      producerEmail: vippsUser?.email ?? null,
      orgnr: vippsUser?.orgnr ?? null,
      year: currentYear,
      comment,
      reportedBy: vippsUser?.phoneNumber ?? "",
      numbers,
    };
  };

  const guardAgainstPublished = async (reportId: string): Promise<boolean> => {
    try {
      const latest = await getReportById(reportId);

      if (latest?.publishDate) {
        setPublishGuardError(
          "Rapporten ble sendt til Fabric mens du hadde siden åpen. Den kan derfor ikke endres lenger."
        );
        setIsEditingConfirmed(false);
        setStep(1);
        refreshBootstrap();

        return false;
      }

      return true;
    } catch (e) {
      console.error("Kunne ikke verifisere rapportstatus før lagring:", e);
      setPublishGuardError(
        "Kunne ikke verifisere rapportstatus før lagring. Prøv igjen, eller last siden på nytt."
      );
      return false;
    }
  };

  const saveDraft = async () => {
    const payload = buildApiPayload();

    if (payload.reportId) {
      const ok = await guardAgainstPublished(payload.reportId);
      if (!ok) return;
    }

    try {
      setIsSavingDraft(true);
      const saved = await saveReport(payload);
      setSavedReport(saved);
    } catch (err) {
      console.error("Feil ved lagring av utkast:", err);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const submitFinalReport = async () => {
    const validationErrors = validateReport(categoryBlocks);
    if (validationErrors.length > 0) {
      setValidationErrors(validationErrors);
      return;
    }

    setSubmitStatus("loading");

    try {
      const payload = buildApiPayload();

      if (payload.reportId) {
        const ok = await guardAgainstPublished(payload.reportId);
        if (!ok) {
          setSubmitStatus("error");
          return;
        }
      }

      const saved = await saveReport(payload);
      setSavedReport(saved);

      const needsConfirm = !saved.reportDate;

      if (needsConfirm) {
        if (!vippsUser?.phoneNumber) {
          setSubmitStatus("error");
          return;
        }

        if (saved.reportId) {
          const ok2 = await guardAgainstPublished(saved.reportId);
          if (!ok2) {
            setSubmitStatus("error");
            return;
          }
        }

        await confirmReport({
          reportId: saved.reportId,
          reporterName: vippsUser.phoneNumber,
          phoneNumber: vippsUser.phoneNumber,
        });
      }

      setSubmitStatus("success");
      setStep(3);

      if (isEditingConfirmed) {
        setIsEditingConfirmed(false);
        refreshBootstrap();
      }
    } catch (err) {
      console.error("Feil ved innsending:", err);
      setSubmitStatus("error");
    }
  };

  const buildNumbersMap = (
    r: SavedReport
  ): Map<string, { name: string; volume: number }> => {
    const map = new Map<string, { name: string; volume: number }>();
    (r.numbers ?? []).forEach((n) => {
      const key = normalizeProductKey(n.productName);
      if (!key) return;
      const prev = map.get(key);
      map.set(key, {
        name: n.productName,
        volume: (prev?.volume ?? 0) + (n.volume ?? 0),
      });
    });
    return map;
  };

  const diffReports = (
    draft: SavedReport,
    confirmed: SavedReport
  ): ReportDiff => {
    const d = buildNumbersMap(draft);
    const c = buildNumbersMap(confirmed);

    const changed: DiffRow[] = [];
    const added: DiffRow[] = [];
    const removed: DiffRow[] = [];

    const allKeys = new Set<string>([...d.keys(), ...c.keys()]);

    for (const key of allKeys) {
      const dn = d.get(key);
      const cn = c.get(key);

      if (dn && cn) {
        if (dn.volume !== cn.volume) {
          changed.push({
            productName: dn.name || cn.name,
            draftVolume: dn.volume,
            confirmedVolume: cn.volume,
          });
        }
      } else if (dn && !cn) {
        added.push({
          productName: dn.name,
          draftVolume: dn.volume,
          confirmedVolume: null,
        });
      } else if (!dn && cn) {
        removed.push({
          productName: cn.name,
          draftVolume: null,
          confirmedVolume: cn.volume,
        });
      }
    }

    const sortByName = (a: DiffRow, b: DiffRow) =>
      a.productName.localeCompare(b.productName, "nb");
    changed.sort(sortByName);
    added.sort(sortByName);
    removed.sort(sortByName);

    return {
      changed,
      added,
      removed,
      hasAny: changed.length + added.length + removed.length > 0,
    };
  };

  const mergeDraftIntoConfirmedPayload = (
    draft: SavedReport,
    confirmed: SavedReport
  ): { productName: string; volume: number }[] => {
    const d = buildNumbersMap(draft);
    const c = buildNumbersMap(confirmed);
    const merged = new Map<string, { name: string; volume: number }>();
    for (const [key, val] of c.entries()) merged.set(key, val);
    for (const [key, dVal] of d.entries()) {
      const cVal = c.get(key);
      if (!cVal) {
        merged.set(key, dVal);
      } else if (cVal.volume !== dVal.volume) {
        merged.set(key, dVal);
      } else {
        // if identical -> ignore it 🤷‍♂️
      }
    }

    return Array.from(merged.values()).map((x) => ({
      productName: x.name,
      volume: x.volume,
    }));
  };

  const softDeleteReport = async (params: {
    reportId: string;
    producerId: string;
  }) => {
    return await saveReport({
      reportId: params.reportId,
      producerId: params.producerId,
      producerName: vippsUser?.producerName ?? null,
      producerEmail: vippsUser?.email ?? null,
      orgnr: vippsUser?.orgnr ?? null,
      year: 0,
      comment: null,
      reportedBy: vippsUser?.phoneNumber ?? "",
      numbers: [],
    });
  };

  const startEditConfirmed = (report: SavedReport) => {
    if (report.publishDate) return;

    setIsEditingConfirmed(true);
    setSavedReport(report);
    setComment(report.comment ?? "");
    setCategoryBlocks(buildCategoryBlocksFromReport(report, productCategories));
    setStep(1);
    setActiveTab("Innrapportering");
  };

  const cancelEditConfirmed = () => {
    setIsEditingConfirmed(false);
    setStep(1);
    refreshBootstrap();
  };

  const isBlockedByPublished =
    currentYearStatus.kind === "conflict" &&
    currentYearStatus.confirmedPublished;
  const shouldShowEditor =
    !isBlockedByPublished &&
    currentYearStatus.kind !== "conflict" &&
    (currentYearStatus.kind !== "confirmed" || isEditingConfirmed);

  const renderConfirmedNumbersTable = (report: SavedReport) => {
    const numbers = report.numbers ?? [];

    if (numbers.length === 0) {
      return (
        <DashboardAlertBox
          type="warning"
          label="Ingen produkter registrert i rapporten."
        />
      );
    }

    // Sort by category + product name (nice UX)
    const rows = numbers
      .map((n) => ({
        productName: n.productName,
        category:
          getCategoryForProduct(n.productName, productCategories) ?? "—",
        volume: n.volume,
        numberId: n.numberId ?? `${n.productName}-${n.volume}`,
      }))
      .sort((a, b) => {
        if (a.category === b.category)
          return a.productName.localeCompare(b.productName, "nb");
        return a.category.localeCompare(b.category, "nb");
      });

    return (
      <div className="overflow-x-auto">
        <table className="dashboardTable reportTable">
          <thead>
            <tr>
              <th>Produkt</th>
              <th>Produktkategori</th>
              <th>{`Volum i ${currentYear}`}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.numberId}>
                <td>{r.productName}</td>
                <td>{r.category}</td>
                <td className="font-semibold">{formatVolumeKg(r.volume)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const showDashboardShell = userAuthenticated && !isAuthLoading;
  const showDashboardContent = showDashboardShell && isPageBootstrapped;

  return (
    <div className="relative">
      <UserInfo
        user={vippsUser}
        loginError={loginError}
        isAuthLoading={isAuthLoading}
      />

      {isAuthLoading && <DashboardLoader />}
      {!isAuthLoading && !userAuthenticated && null}

      {showDashboardShell && (
        <div id="dashboard" className="mb-40">
          <Tabs
            tabs={actorTabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            disabled={currentYearStatus.kind === "conflict"}
          />
          <div
            id="dashboard-inner"
            className="flex flex-col gap-5 tablet:gap-10 mt-10"
          >
            {!showDashboardContent ? (
              <DashboardLoader label="Laster rapportdata…" />
            ) : (
              <>
                {currentYearStatus.kind === "conflict" && (
                  <div className="flex flex-col gap-5">
                    <DashboardAlertBox
                      type="warning"
                      label="Vi fant både et utkast og en bekreftet rapport for inneværende år. Velg handling nedenfor som passer best."
                    />

                    <DashboardCard as="section" className="flex flex-col gap-5">
                      <div className="flex flex-col gap-3">
                        <h2 className="text-4xl font-semibold leading-tight tablet:font-5xl">
                          Konflikt funnet i rapport
                        </h2>
                        <p className="text-black-subtile">
                          Dette er kun en sikkerhetsmekanisme for å forsikre seg
                          at du ikke sender inn to separate rapporter.
                        </p>
                      </div>

                      {currentYearStatus.confirmedPublished ? (
                        <DashboardAlertBox
                          type="info"
                          label="Den bekreftede rapporten er allerede sendt til Fabric og kan ikke endres. Du kan slette utkastet."
                        />
                      ) : (
                        <DashboardAlertBox
                          type="info"
                          label="Den bekreftede rapporten kan fortsatt redigeres. Du kan slå sammen utkastet inn i rapporten som er bekreftet."
                        />
                      )}

                      {conflictActionError && (
                        <DashboardAlertBox
                          type="error"
                          label={conflictActionError}
                        />
                      )}

                      {/* Diff (keep it small and only show if differences exist) */}
                      {currentYearStatus.diff.hasAny ? (
                        <div className="flex flex-col gap-3">
                          <h3 className="text-lg font-semibold">Forskjeller</h3>

                          <div className="overflow-x-auto">
                            <table className="dashboardTable reportTable">
                              <thead>
                                <tr>
                                  <th>Produkt</th>
                                  <th>
                                    <span className="whitespace-nowrap">
                                      Bekreftet tall
                                    </span>
                                    {isMobile ? <br /> : " "}
                                    <span className="font-normal">
                                      (
                                      {formatDate(
                                        currentYearStatus.confirmed.reportDate
                                      )}
                                      )
                                    </span>
                                  </th>
                                  <th>Fra utkast</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentYearStatus.diff.changed.map((r) => (
                                  <tr key={`chg-${r.productName}`}>
                                    <td>{r.productName}</td>
                                    <td>
                                      {formatVolumeKg(r.confirmedVolume, {
                                        empty: "–",
                                      })}
                                    </td>
                                    <td className="font-semibold">
                                      {formatVolumeKg(r.draftVolume, {
                                        empty: "–",
                                      })}
                                    </td>
                                  </tr>
                                ))}
                                {currentYearStatus.diff.added.map((r) => (
                                  <tr key={`add-${r.productName}`}>
                                    <td>{r.productName}</td>
                                    <td>–</td>
                                    <td className="font-semibold">
                                      {formatVolumeKg(r.draftVolume, {
                                        empty: "–",
                                      })}
                                    </td>
                                  </tr>
                                ))}
                                {currentYearStatus.diff.removed.map((r) => (
                                  <tr key={`rem-${r.productName}`}>
                                    <td>{r.productName}</td>
                                    <td className="font-semibold">
                                      {formatVolumeKg(r.confirmedVolume, {
                                        empty: "–",
                                      })}
                                    </td>
                                    <td>–</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <p className="text-black-subtile">
                          Ingen forskjeller funnet mellom utkast og bekreftet
                          rapport.
                        </p>
                      )}

                      <div className="flex flex-row gap-3 mt-5 justify-center">
                        <DashboardButton
                          label="Slett utkast"
                          variant="underline"
                          icon="icon-trash"
                          disabled={isResolvingConflict}
                          onClick={async () => {
                            try {
                              setIsResolvingConflict(true);
                              setConflictActionError(null);

                              await softDeleteReport({
                                reportId: currentYearStatus.draft.reportId,
                                producerId: vippsUser?.producerId ?? "",
                              });

                              refreshBootstrap();
                              setCurrentYearStatus({ kind: "none" });
                              setStep(1);
                            } catch (e) {
                              console.error(e);
                              setConflictActionError(
                                e instanceof Error
                                  ? e.message
                                  : "Kunne ikke slette utkastet."
                              );
                            } finally {
                              setIsResolvingConflict(false);
                            }
                          }}
                        />
                        {!currentYearStatus.confirmedPublished && (
                          <DashboardButton
                            label="Slå sammen utkast"
                            variant="darkplain"
                            icon="icon-check"
                            loading={isResolvingConflict}
                            loadingText="Fikser…"
                            disabled={isResolvingConflict}
                            onClick={async () => {
                              try {
                                setIsResolvingConflict(true);
                                setConflictActionError(null);

                                const mergedNumbers =
                                  mergeDraftIntoConfirmedPayload(
                                    currentYearStatus.draft,
                                    currentYearStatus.confirmed
                                  );

                                const saved = await saveReport({
                                  reportId:
                                    currentYearStatus.confirmed.reportId,
                                  producerId: vippsUser?.producerId ?? "",
                                  producerName: vippsUser?.producerName ?? null,
                                  producerEmail: vippsUser?.email ?? null,
                                  orgnr: vippsUser?.orgnr ?? null,
                                  year: currentYear,
                                  comment:
                                    currentYearStatus.draft.comment ??
                                    currentYearStatus.confirmed.comment ??
                                    null,
                                  reportedBy: vippsUser?.phoneNumber ?? "",
                                  numbers: mergedNumbers,
                                });

                                await softDeleteReport({
                                  reportId: currentYearStatus.draft.reportId,
                                  producerId: vippsUser?.producerId ?? "",
                                });

                                setSavedReport(saved);
                                setCategoryBlocks(
                                  buildCategoryBlocksFromReport(
                                    saved,
                                    productCategories
                                  )
                                );
                                setComment(saved.comment ?? "");
                                setCurrentYearStatus({
                                  kind: "draft",
                                  report: saved,
                                });
                                setStep(1);
                              } catch (e) {
                                console.error(e);
                                setConflictActionError(
                                  e instanceof Error
                                    ? e.message
                                    : "Kunne ikke slå sammen utkastet."
                                );
                              } finally {
                                setIsResolvingConflict(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    </DashboardCard>
                  </div>
                )}

                {activeTab === "Innrapportering" && (
                  <div
                    className={cn(
                      "flex flex-col gap-5 w-full mx-auto",
                      step !== 3 && "max-w-[932px]"
                    )}
                  >
                    {isLoadingBootstrap ||
                      publishGuardError ||
                      (currentYearStatus.kind === "draft" &&
                        !isEditingConfirmed &&
                        step === 1 && (
                          <div className="flex flex-col gap-1">
                            {isLoadingBootstrap && (
                              <DashboardAlertBox
                                type="info"
                                label="Laster rapportdata…"
                              />
                            )}
                            {publishGuardError && (
                              <DashboardAlertBox
                                type="error"
                                label={publishGuardError}
                              />
                            )}
                            {currentYearStatus.kind === "draft" &&
                              !isEditingConfirmed &&
                              step === 1 && (
                                <DashboardAlertBox
                                  type="info"
                                  label="Du har et pågående utkast for inneværende år. Du kan fortsette der du slapp."
                                />
                              )}
                          </div>
                        ))}

                    {currentYearStatus.kind === "confirmed" &&
                      !isEditingConfirmed && (
                        <DashboardCard
                          as="section"
                          className="flex flex-col gap-5 tablet:gap-10"
                        >
                          <div className="flex flex-col gap-3">
                            <h2 className="text-4xl font-semibold leading-tight tablet:font-5xl">
                              Fjorårets rapport er levert
                            </h2>
                            <p className="text-xl leading-[30px]">
                              Du eller virksomheten din har levert rapporten for
                              fjoråret. Dersom du ønsker å gjøre endringer i den
                              innsendte rapporten, klikk på «Rediger rapport».
                            </p>
                          </div>

                          {renderConfirmedNumbersTable(
                            currentYearStatus.report
                          )}

                          <div className="flex flex-col gap-3">
                            <h3 className="text-2xl leading-8 font-semibold">
                              Kommentar
                            </h3>
                            {currentYearStatus.report.comment ? (
                              <p className="text-base leading-6">
                                {currentYearStatus.report.comment}
                              </p>
                            ) : (
                              <DashboardAlertBox
                                type="neutral"
                                label="Ingen kommentar lagt til på rapporten."
                              />
                            )}
                          </div>

                          {currentYearStatus.report.publishDate ? (
                            <DashboardAlertBox
                              type="neutral"
                              label="Rapporten er nå låst for redigering."
                            />
                          ) : (
                            <div className="flex gap-3 justify-center">
                              <DashboardButton
                                label="Rediger rapport"
                                variant="sidebar"
                                icon="icon-square-pen"
                                rounded="full"
                                className="min-h-[54px]"
                                onClick={() =>
                                  startEditConfirmed(currentYearStatus.report)
                                }
                              />
                            </div>
                          )}
                        </DashboardCard>
                      )}

                    {currentYearStatus.kind === "confirmed" &&
                      isEditingConfirmed && (
                        <DashboardAlertBox
                          type="info"
                          label="Du redigerer en innsendt rapport. Endringer lagres når du fullfører."
                        />
                      )}

                    {shouldShowEditor && (
                      <>
                        {step === 1 && (
                          <ReportInputView
                            categoryBlocks={categoryBlocks}
                            updateCategory={updateCategory}
                            updateProduct={updateProduct}
                            updateVolume={updateVolume}
                            updateLastYearVolume={updateLastYearVolume}
                            setRowErrors={setRowErrors}
                            addCategoryBlock={addCategoryBlock}
                            removeCategoryBlock={removeCategoryBlock}
                            hasRowErrors={hasRowErrors}
                            nextStep={nextStep}
                            onSaveDraft={saveDraft}
                            isSavingDraft={isSavingDraft}
                            lastYearVolumesByProduct={lastYearVolumesByProduct}
                            hasMissingVolume={() =>
                              hasMissingVolume(categoryBlocks)
                            }
                            hasValidProductEntry={() =>
                              hasValidProductEntry(categoryBlocks)
                            }
                            showEditCancelButton={
                              currentYearStatus.kind === "confirmed" &&
                              isEditingConfirmed
                            }
                            editCancelButton={cancelEditConfirmed}
                          />
                        )}

                        {step === 2 && (
                          <ReportSummaryView
                            categoryBlocks={categoryBlocks}
                            comment={comment}
                            setComment={setComment}
                            prevStep={prevStep}
                            submitFinalReport={submitFinalReport}
                            submitStatus={submitStatus}
                            showEditCancelButton={
                              currentYearStatus.kind === "confirmed" &&
                              isEditingConfirmed
                            }
                            editCancelButton={cancelEditConfirmed}
                          />
                        )}

                        {step === 3 && (
                          <ReportSuccessView
                            onGoToReportHistory={goToReportHistory}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}

                {activeTab === "Rapporthistorikk" && (
                  <div className="flex flex-col gap-5 w-full mx-auto max-w-[932px]">
                    <ReportHistoryView
                      currentYearReport={reportsByYear[currentYear] ?? null}
                      previousYearReport={reportsByYear[previousYear] ?? null}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActorDashboardPage;
